import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import type { userListFilterIProps } from "~/entity/Users/model/schema";
import { useUrlFilters } from "~/features/filter";

import { usersFilterFields } from "./fields";

type UsersFilterValues = Pick<
  userListFilterIProps,
  | "id"
  | "fullName"
  | "searchText"
  | "position"
  | "email"
  | "phone"
  | "dealerIds"
  | "roleNames"
  | "permissionIds"
  | "subdivisionIds"
  | "status"
  | "statuses"
>;

const toStringArray = (value: unknown): string[] | undefined => {
  const list =
    value == null || value === "" ? [] : Array.isArray(value) ? value : [value];
  const strings = list.map(String).filter(Boolean);
  return strings.length ? strings : undefined;
};

const toNumberArray = (value: unknown): number[] | undefined => {
  const numbers = toStringArray(value)
    ?.map(Number)
    .filter((n) => !Number.isNaN(n));
  return numbers?.length ? numbers : undefined;
};

/**
 * Возвращает значения фильтра пользователей из URL,
 * приведённые к типам `userListFilterIProps` (готовые к передаче в API).
 */
export const useUsersFilterValues = (): UsersFilterValues => {
  const { t } = useTranslation();
  const fields = useMemo(() => usersFilterFields(t), [t]);
  const { values } = useUrlFilters(fields);

  return useMemo<UsersFilterValues>(() => {
    const idRaw = values.id as string | undefined;
    const idNum = idRaw != null && idRaw !== "" ? Number(idRaw) : undefined;

    return {
      id: typeof idNum === "number" && !Number.isNaN(idNum) ? idNum : undefined,
      fullName: (values.fullName as string) || undefined,
      searchText: (values.searchText as string) || undefined,
      position: (values.position as string) || undefined,
      email: (values.email as string) || undefined,
      phone: (values.phone as string) || undefined,
      dealerIds: toNumberArray(values.dealerIds),
      roleNames: toStringArray(values.roleNames),
      permissionIds: toStringArray(values.permissionIds),
      subdivisionIds: toNumberArray(values.subdivisionIds),
      status: (values.status as string) || toStringArray(values.statuses)?.[0],
      statuses: toStringArray(values.statuses),
    };
  }, [values]);
};
